import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";

// Marketing
import MarketingLayout from "@/pages/marketing/MarketingLayout";
import MarketingHome from "@/pages/marketing/MarketingHome";
import ExamPage from "@/pages/marketing/ExamPage";
import { CitiesIndex, CityPage } from "@/pages/marketing/CityPages";
import { BlogIndex, BlogPost } from "@/pages/marketing/Blog";
import { Features, HowItWorks, Pricing, Results, FAQ, Contact } from "@/pages/marketing/OtherPages";

// Auth
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";

// Portal (existing)
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import QuestionBank from "@/pages/admin/QuestionBank";
import ImportWizard from "@/pages/admin/ImportWizard";
import ManualQuestion from "@/pages/admin/ManualQuestion";
import TestBuilder from "@/pages/admin/TestBuilder";
import AdminTests from "@/pages/admin/AdminTests";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminStudents from "@/pages/admin/AdminStudents";
import StudentLayout from "@/pages/student/StudentLayout";
import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentTests from "@/pages/student/StudentTests";
import LiveExam from "@/pages/student/LiveExam";
import Result from "@/pages/student/Result";
import Rewards from "@/pages/student/Rewards";
import StudentProfile from "@/pages/student/StudentProfile";
import ParentLayout from "@/pages/parent/ParentLayout";
import ParentDashboard from "@/pages/parent/ParentDashboard";
import ParentAssign from "@/pages/parent/ParentAssign";
import ParentNotifications from "@/pages/parent/ParentNotifications";
import ParentDigest from "@/pages/parent/ParentDigest";
import Podcasts from "@/pages/student/Podcasts";
import DuelChallenge from "@/pages/DuelChallenge";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          {/* Marketing site */}
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<MarketingHome />} />
            <Route path="/jee" element={<ExamPage />} />
            <Route path="/neet" element={<ExamPage />} />
            <Route path="/olympiads" element={<ExamPage />} />
            <Route path="/govt-exams" element={<ExamPage />} />
            <Route path="/features" element={<Features />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/results" element={<Results />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/cities" element={<CitiesIndex />} />
            <Route path="/cities/:slug" element={<CityPage />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
          </Route>

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* Portals */}
          <Route path="/admin" element={<Protected roles={["admin"]}><AdminLayout /></Protected>}>
            <Route index element={<AdminDashboard />} />
            <Route path="questions" element={<QuestionBank />} />
            <Route path="import" element={<ImportWizard />} />
            <Route path="questions/new" element={<ManualQuestion />} />
            <Route path="tests" element={<AdminTests />} />
            <Route path="tests/new" element={<TestBuilder />} />
            <Route path="students" element={<AdminStudents />} />
            <Route path="analytics" element={<AdminAnalytics />} />
          </Route>

          <Route path="/student" element={<Protected roles={["student"]}><StudentLayout /></Protected>}>
            <Route index element={<StudentDashboard />} />
            <Route path="tests" element={<StudentTests />} />
            <Route path="podcasts" element={<Podcasts />} />
            <Route path="rewards" element={<Rewards />} />
            <Route path="profile" element={<StudentProfile />} />
            <Route path="results/:attemptId" element={<Result />} />
          </Route>
          <Route path="/student/exam/:testId" element={<Protected roles={["student"]}><LiveExam /></Protected>} />
          <Route path="/duel/:code" element={<DuelChallenge />} />

          <Route path="/parent" element={<Protected roles={["parent"]}><ParentLayout /></Protected>}>
            <Route index element={<ParentDashboard />} />
            <Route path="digest" element={<ParentDigest />} />
            <Route path="assign" element={<ParentAssign />} />
            <Route path="notifications" element={<ParentNotifications />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
